import { z } from 'zod';
import { directorNoteDataSchema, directorSuggestionDataSchema } from './director-ai.js';
import { calendarGapDataSchema, openReviewsDataSchema, missingMediaDataSchema, publicationFailedDataSchema, contentMixDeviationDataSchema, apartmentNotFeaturedDataSchema, contentDueUnapprovedDataSchema, entryNotFeaturedDataSchema, contentMixReviewDataSchema, strategyReviewDataSchema, seasonalTopicDataSchema, topicProposalDataSchema, researchSuggestionDataSchema, analyticsPermissionMissingDataSchema, creditsLowDataSchema, monthlyReportDataSchema, occasionReadyDataSchema, photoNeededDataSchema, } from './inbox-kind-data.js';
/**
 * Inbox message kinds of module 110 (spec 110 §4.1, S110-1 T110-1-3): the
 * value sets stored in `inbox_messages`, each kind's default severity and
 * allowed actions, and the zod schema of its `data`. The web renders title
 * and body from `kind` + `data` with i18n keys (`inbox.kind.<kind>`), so
 * `data` carries IDs and numbers, never display text (spec 110 §6).
 *
 * A kind without a data schema here cannot be stored yet: the story that
 * raises it (S110-2 .. S110-5, module 120) adds the schema with its check.
 */
export const INBOX_MESSAGE_KINDS = [
    'calendar_gap',
    'open_reviews',
    'missing_media',
    'publication_failed',
    'content_mix_deviation',
    'apartment_not_featured',
    'seasonal_topic',
    'topic_proposal',
    'content_mix_review',
    'strategy_review',
    'director_suggestion',
    'director_note',
    'research_suggestion',
    'analytics_permission_missing',
    'credits_low',
    'monthly_report',
    'occasion_ready',
    'photo_needed',
    'entry_not_featured',
    'content_due_unapproved',
];
/**
 * The kinds the advice tool `list_inbox_messages` may return (spec 110 section 4.15.3): pure content advice.
 * Billing (`credits_low`), account and permission state (`analytics_permission_missing`) and publishing
 * failures (`publication_failed`) stay out of the catalog, for external read tokens and the assistant alike.
 */
export const INBOX_ADVICE_KINDS = INBOX_MESSAGE_KINDS.filter((kind) => !['credits_low', 'analytics_permission_missing', 'publication_failed'].includes(kind));
export const INBOX_SEVERITIES = ['info', 'warning', 'critical'];
export const INBOX_ORIGINS = ['system', 'ai_job'];
export const INBOX_STATUSES = ['open', 'done', 'dismissed'];
export const INBOX_RESOLUTIONS = ['accepted', 'regenerated', 'campaign_created', 'dismissed', 'auto_resolved', 'superseded'];
/** `apartment` stays for messages stored before E470; new ones name an `entry`. */
export const INBOX_SUBJECT_TYPES = ['content_item', 'publication', 'apartment', 'content_pillar', 'theme_slot', 'campaign', 'social_account', 'entry'];
export const INBOX_ACTION_TYPES = ['view', 'accept', 'regenerate', 'choose_media', 'ignore', 'create_campaign'];
/**
 * The actions a client posts to `POST /v1/inbox-messages/{id}/actions`
 * (spec 110 §5); `view` and `choose_media` are client-side navigation and are
 * never sent. S110-4 executes `ignore`, S110-5 `accept`, `regenerate` and
 * `create_campaign` for the kinds that have a handler.
 */
export const INBOX_SERVER_ACTION_TYPES = ['accept', 'regenerate', 'create_campaign', 'ignore'];
/**
 * Permissions the acting member needs per server action (spec 110 §4.1), for
 * the kinds that need nothing special. The server checks them; the web only
 * uses them to hide buttons a member cannot use. Use `inboxActionPermissions`
 * for a message: `accept` of a research suggestion needs `knowledge:write`,
 * `regenerate` of a topic proposal also archives the idea (`content:write`).
 */
export const INBOX_ACTION_PERMISSIONS = {
    accept: ['content:write', 'ai:run'],
    regenerate: ['ai:run'],
    create_campaign: ['content:write'],
    ignore: ['content:write'],
};
/** The permissions `type` needs on a message of `kind` (spec 110 §4.1 "Permissions" column). */
export function inboxActionPermissions(type, kind) {
    if (type === 'accept' && kind === 'research_suggestion')
        return ['knowledge:write'];
    // Both rewrite a post: a topic proposal archives its idea, an occasion post is finalised again (spec 110 section 4.9).
    if (type === 'regenerate' && (kind === 'topic_proposal' || kind === 'occasion_ready'))
        return ['ai:run', 'content:write'];
    return INBOX_ACTION_PERMISSIONS[type];
}
/**
 * Every permission set that lets `type` pass for some kind: the member needs at
 * least one of them before the message is even looked at (403 before 404),
 * then exactly `inboxActionPermissions(type, message.kind)`.
 */
export function inboxActionPermissionAlternatives(type) {
    return INBOX_MESSAGE_KINDS.reduce((sets, kind) => {
        const needed = inboxActionPermissions(type, kind);
        return sets.some((set) => set.length === needed.length && set.every((permission) => needed.includes(permission))) ? sets : [...sets, needed];
    }, []);
}
/**
 * What the accept action prepares for an idea (S110-5, spec 110 §4.1): two feed
 * formats, in the tenant's enabled languages except the German source. The
 * editor changes formats and languages afterwards in the content editor.
 */
export const INBOX_ACCEPT_FORMATS = ['ig_feed', 'fb_feed'];
/**
 * The formats of a reel idea (E400): an Instagram reel with a Facebook photo post. The Facebook reel
 * joins once it mirrors the Instagram storyboard (S260-5); until then Facebook gets the feed post.
 */
export const INBOX_REEL_FORMATS = ['ig_reel', 'fb_feed'];
/** Ideas a regenerate run asks for when the message does not name a number (spec 110 §4.1). */
export const INBOX_REGENERATE_DEFAULT_COUNT = 5;
/** The window a regenerate run plans when the message names none (`entry_not_featured`, `apartment_not_featured`): today and the 27 days after. */
export const INBOX_REGENERATE_WINDOW_DAYS = 28;
/**
 * The `origin` marker module 110 sets on the AI runs it enqueues itself
 * (module 80's `AiJobRequest.origin`, S110-5): the `topic_planner` runs of
 * `regenerate` and the routine's `content_director` runs. Its
 * `AiRunFinishedListener` recognizes its own `topic_planner` runs by it
 * (T110-5-5). The preparation chain started by `accept` is module 80's and
 * carries no marker.
 */
export const DIRECTOR_AI_RUN_ORIGIN = 'director';
export const inboxActionSchema = z
    .object({ type: z.enum(INBOX_ACTION_TYPES), params: z.record(z.string(), z.unknown()).optional() })
    .strict();
const definition = (kind, severity, allowedActions, data = null) => ({ kind, severity, allowedActions, data });
/** Spec 110 §4.1, one row per kind. */
export const INBOX_KINDS = {
    calendar_gap: definition('calendar_gap', 'warning', ['regenerate', 'view', 'ignore'], calendarGapDataSchema),
    open_reviews: definition('open_reviews', 'info', ['view', 'ignore'], openReviewsDataSchema),
    missing_media: definition('missing_media', 'warning', ['choose_media', 'view', 'ignore'], missingMediaDataSchema),
    publication_failed: definition('publication_failed', 'critical', ['view', 'ignore'], publicationFailedDataSchema),
    content_mix_deviation: definition('content_mix_deviation', 'info', ['regenerate', 'view', 'ignore'], contentMixDeviationDataSchema),
    apartment_not_featured: definition('apartment_not_featured', 'info', ['regenerate', 'view', 'ignore'], apartmentNotFeaturedDataSchema),
    seasonal_topic: definition('seasonal_topic', 'info', ['regenerate', 'create_campaign', 'view', 'ignore'], seasonalTopicDataSchema),
    topic_proposal: definition('topic_proposal', 'info', ['accept', 'regenerate', 'view', 'ignore'], topicProposalDataSchema),
    content_mix_review: definition('content_mix_review', 'info', ['view', 'ignore'], contentMixReviewDataSchema),
    strategy_review: definition('strategy_review', 'info', ['view', 'ignore'], strategyReviewDataSchema),
    director_suggestion: definition('director_suggestion', 'info', ['accept', 'create_campaign', 'view', 'ignore'], directorSuggestionDataSchema),
    director_note: definition('director_note', 'info', ['view', 'ignore'], directorNoteDataSchema),
    research_suggestion: definition('research_suggestion', 'info', ['accept', 'view', 'ignore'], researchSuggestionDataSchema),
    analytics_permission_missing: definition('analytics_permission_missing', 'warning', ['view', 'ignore'], analyticsPermissionMissingDataSchema),
    credits_low: definition('credits_low', 'warning', ['view', 'ignore'], creditsLowDataSchema),
    monthly_report: definition('monthly_report', 'info', ['view', 'ignore'], monthlyReportDataSchema),
    occasion_ready: definition('occasion_ready', 'info', ['view', 'choose_media', 'regenerate', 'ignore'], occasionReadyDataSchema),
    photo_needed: definition('photo_needed', 'info', ['view', 'ignore'], photoNeededDataSchema),
    entry_not_featured: definition('entry_not_featured', 'info', ['regenerate', 'view', 'ignore'], entryNotFeaturedDataSchema),
    content_due_unapproved: definition('content_due_unapproved', 'warning', ['view', 'ignore'], contentDueUnapprovedDataSchema),
};
/** Why an inbox message cannot be stored as given (a programming error of the raising check, not user input). */
export function inboxMessageProblems(kind, data, actions) {
    const kindDefinition = INBOX_KINDS[kind];
    const problems = [];
    if (!kindDefinition.data) {
        problems.push(`kind ${kind} has no data schema yet`);
    }
    else if (!kindDefinition.data.safeParse(data).success) {
        problems.push(`data does not match the ${kind} schema`);
    }
    for (const action of actions) {
        if (!inboxActionSchema.safeParse(action).success)
            problems.push('malformed action');
        else if (!kindDefinition.allowedActions.includes(action.type))
            problems.push(`action ${action.type} is not allowed for ${kind}`);
    }
    return problems;
}
//# sourceMappingURL=inbox-kinds.js.map